/**
 * Collection PLP behaviour — the vanilla replacement for Algolia InstantSearch.
 *
 * Everything here re-fetches `main-collection` through the Section Rendering
 * API and swaps the markup in place, so filtering and sorting feel the same as
 * the old client-side widgets while the first paint stays server-rendered.
 *
 * Responsibilities:
 *   - `ais-results-size-*` breakpoint class (ported from `readjust()`; the
 *     mobile filter drawer is driven entirely by it)
 *   - "Show filters" / "Hide filters" toggle
 *   - filter links and the sort <select>
 *   - auto load-more, standing in for Algolia's `hitsPerPage: 500`
 *   - a shim translating legacy `?refinementList[...]` URLs to `?filter.p.tag=`
 *   - the two-handle price slider
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-collection-results]';
  var PAGE_SELECTOR = '.ais-page';

  var loadingMore = false;

  function root() {
    return document.querySelector(ROOT_SELECTOR);
  }

  /* ----------------------------------------------------------------------- *
   * Breakpoint class
   * ----------------------------------------------------------------------- */

  function readjust() {
    var el = root();
    if (!el) return;

    var width = el.offsetWidth;
    var suffix = 'lg';
    if (width < 400) suffix = 'xs';
    else if (width < 800) suffix = 'sm';
    else if (width < 1200) suffix = 'md';

    el.classList.remove(
      'ais-results-size-xs',
      'ais-results-size-sm',
      'ais-results-size-md',
      'ais-results-size-lg'
    );
    el.classList.add('ais-results-size-' + suffix);
  }

  /* ----------------------------------------------------------------------- *
   * Legacy Algolia URL shim
   *
   * Old links look like:
   *   ?refinementList%5Bnamed_tags.Pet%20Type%5D%5B0%5D=Dogs
   * which has to become:
   *   ?filter.p.tag=Pet+Type:Dogs
   *
   * Runs before anything else so a deep link from an email or an ad lands on
   * the right filtered view instead of the bare collection.
   * ----------------------------------------------------------------------- */

  function migrateLegacyParams() {
    var search = window.location.search;
    if (search.indexOf('refinementList') === -1 && search.indexOf('range') === -1) {
      return false;
    }

    var current = new URLSearchParams(search);
    var next = new URLSearchParams();
    var changed = false;

    current.forEach(function (value, key) {
      var facetMatch = key.match(/^refinementList\[([^\]]+)\]\[\d+\]$/);
      if (facetMatch) {
        var attribute = facetMatch[1];
        var namedTag = attribute.match(/^named_tags\.(.+)$/);
        if (namedTag) {
          next.append('filter.p.tag', namedTag[1] + ':' + value);
        } else if (attribute === 'vendor') {
          next.append('filter.p.vendor', value);
        } else if (attribute === 'product_type') {
          next.append('filter.p.product_type', value);
        }
        changed = true;
        return;
      }

      // range[price]=5:55 — Algolia's slider serialisation.
      var rangeMatch = key.match(/^range\[(.+)\]$/);
      if (rangeMatch && rangeMatch[1] === 'price') {
        var bounds = String(value).split(':');
        if (bounds[0]) next.append('filter.v.price.gte', bounds[0]);
        if (bounds[1]) next.append('filter.v.price.lte', bounds[1]);
        changed = true;
        return;
      }

      next.append(key, value);
    });

    if (!changed) return false;

    window.location.replace(
      window.location.pathname + '?' + next.toString() + window.location.hash
    );
    return true;
  }

  /* ----------------------------------------------------------------------- *
   * Section rendering
   * ----------------------------------------------------------------------- */

  function sectionUrl(url) {
    var el = root();
    var sectionId = el ? el.dataset.sectionId : null;
    if (!sectionId) return null;

    var parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('section_id', sectionId);
    return parsed.toString();
  }

  function afterSwap() {
    readjust();
    initPriceSliders();
    if (typeof window.refreshCardRatings === 'function') {
      window.refreshCardRatings();
    }
    // Quick-add is a custom element, so nodes inserted here upgrade on their
    // own — nothing to re-initialise.
  }

  function render(url, options) {
    var target = sectionUrl(url);
    if (!target) {
      window.location.assign(url);
      return;
    }

    var el = root();
    el.setAttribute('aria-busy', 'true');

    fetch(target)
      .then(function (response) {
        if (!response.ok) throw new Error('Section render failed');
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var freshPage = parsed.querySelector(PAGE_SELECTOR);
        var currentPage = el.querySelector(PAGE_SELECTOR);
        if (!freshPage || !currentPage) throw new Error('Section markup missing');

        currentPage.replaceWith(freshPage);

        if (!options || options.pushState !== false) {
          window.history.pushState({}, '', url);
        }
        afterSwap();

        if (options && options.scrollToTop) {
          var top = el.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({top: top - 20, behavior: 'smooth'});
        }
      })
      .catch(function () {
        // Never leave the shopper stranded on a half-updated grid.
        window.location.assign(url);
      })
      .then(function () {
        el.removeAttribute('aria-busy');
      });
  }

  /* ----------------------------------------------------------------------- *
   * Load more
   * ----------------------------------------------------------------------- */

  function loadMore() {
    var el = root();
    if (!el || loadingMore) return;

    var sentinel = el.querySelector('[data-load-more]');
    if (!sentinel) return;

    var nextUrl = sentinel.dataset.nextUrl;
    if (!nextUrl) return;

    var target = sectionUrl(nextUrl);
    if (!target) return;

    loadingMore = true;
    sentinel.classList.add('is-loading');

    fetch(target)
      .then(function (response) {
        if (!response.ok) throw new Error('Load more failed');
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var incoming = parsed.querySelector('[data-hits-list]');
        var list = el.querySelector('[data-hits-list]');
        if (!incoming || !list) throw new Error('Hit list missing');

        while (incoming.firstElementChild) {
          list.appendChild(incoming.firstElementChild);
        }

        var nextSentinel = parsed.querySelector('[data-load-more]');
        if (nextSentinel) {
          sentinel.dataset.nextUrl = nextSentinel.dataset.nextUrl;
        } else {
          sentinel.remove();
        }

        syncResultCount();

        if (typeof window.refreshCardRatings === 'function') {
          window.refreshCardRatings();
        }
      })
      .catch(function () {
        // Leave the sentinel in place; the observer retries on the next scroll.
      })
      .then(function () {
        loadingMore = false;
        sentinel.classList.remove('is-loading');
        observeSentinel();
      });
  }

  /* ----------------------------------------------------------------------- *
   * Result count
   *
   * Algolia knew `nbHits` up front because it fetched the whole collection in
   * one request. Server-side we only know the current page, so the count is
   * recomputed from the rendered cards each time load-more appends a batch. It
   * settles on the true total once the collection is fully loaded.
   * ----------------------------------------------------------------------- */

  function syncResultCount() {
    var el = root();
    if (!el) return;

    var label = el.querySelector('[data-results-count]');
    var list = el.querySelector('[data-hits-list]');
    if (!label || !list) return;

    label.textContent = list.children.length + ' ' + (label.dataset.countLabel || 'Products');
  }

  var observer = null;

  function observeSentinel() {
    var el = root();
    if (!el || !('IntersectionObserver' in window)) return;

    var sentinel = el.querySelector('[data-load-more]');
    if (!sentinel) return;

    if (!observer) {
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) loadMore();
          });
        },
        {rootMargin: '600px 0px'}
      );
    }
    observer.observe(sentinel);
  }

  /* ----------------------------------------------------------------------- *
   * Price slider — reproduces rheostat's DOM contract
   * ----------------------------------------------------------------------- */

  function initPriceSliders() {
    var sliders = document.querySelectorAll('[data-price-slider]');
    Array.prototype.forEach.call(sliders, function (slider) {
      if (slider.dataset.sliderReady === 'true') return;
      slider.dataset.sliderReady = 'true';
      setupSlider(slider);
    });
  }

  function setupSlider(slider) {
    // The scale runs from the cheapest product in the result set, not from 0 —
    // see `collection-filter-price.liquid`. Every position is therefore relative
    // to [rangeMin, rangeMax], not to rangeMax alone.
    var rangeMin = Number(slider.dataset.rangeMin) || 0;
    var rangeMax = Number(slider.dataset.rangeMax) || 1;
    var span = Math.max(rangeMax - rangeMin, 1);
    var minParam = slider.dataset.minParam;
    var maxParam = slider.dataset.maxParam;

    var progress = slider.querySelector('.rheostat-progress');
    var handles = slider.querySelectorAll('.rheostat-handle');
    if (handles.length < 2) return;

    function readValue(raw, fallback) {
      var parsed = Number(raw);
      return isNaN(parsed) ? fallback : parsed;
    }

    var state = {
      min: readValue(slider.dataset.selectedMin, rangeMin),
      max: readValue(slider.dataset.selectedMax, rangeMax),
    };

    function pct(value) {
      return ((value - rangeMin) / span) * 100;
    }

    function paint() {
      handles[0].style.left = pct(state.min) + '%';
      handles[1].style.left = pct(state.max) + '%';
      progress.style.left = pct(state.min) + '%';
      progress.style.width = ((state.max - state.min) / span) * 100 + '%';

      var tooltips = slider.querySelectorAll('.rheostat-tooltip');
      if (tooltips[0]) tooltips[0].textContent = Math.round(state.min / 100);
      if (tooltips[1]) tooltips[1].textContent = Math.round(state.max / 100);
    }

    function valueAt(clientX) {
      var box = slider.getBoundingClientRect();
      var ratio = (clientX - box.left) / box.width;
      ratio = Math.min(1, Math.max(0, ratio));
      // Snap to whole currency units, matching the old slider's integer labels.
      var value = Math.round((rangeMin + ratio * span) / 100) * 100;
      return Math.min(rangeMax, Math.max(rangeMin, value));
    }

    function commit() {
      var url = new URL(window.location.href);
      url.searchParams.delete(minParam);
      url.searchParams.delete(maxParam);
      url.searchParams.delete('page');

      // Only send a bound that actually narrows the range, so an untouched
      // slider leaves the URL clean.
      if (state.min > rangeMin) url.searchParams.set(minParam, Math.round(state.min / 100));
      if (state.max < rangeMax) url.searchParams.set(maxParam, Math.round(state.max / 100));

      render(url.toString(), {scrollToTop: false});
    }

    // Clicking the track jumps the nearer handle to that point, the way
    // rheostat behaved. Without this the only way to move a value was to grab a
    // 20px knob exactly.
    slider.addEventListener('pointerdown', function (event) {
      if (event.target.closest('.rheostat-handle')) return;

      var value = valueAt(event.clientX);
      var distanceToMin = Math.abs(value - state.min);
      var distanceToMax = Math.abs(value - state.max);

      if (distanceToMin <= distanceToMax) {
        state.min = Math.min(value, state.max);
      } else {
        state.max = Math.max(value, state.min);
      }

      paint();
      commit();
    });

    Array.prototype.forEach.call(handles, function (handle, index) {
      handle.addEventListener('pointerdown', function (event) {
        event.preventDefault();
        handle.setPointerCapture(event.pointerId);

        function onMove(moveEvent) {
          var value = valueAt(moveEvent.clientX);
          if (index === 0) {
            state.min = Math.min(value, state.max);
          } else {
            state.max = Math.max(value, state.min);
          }
          paint();
        }

        function onUp() {
          handle.removeEventListener('pointermove', onMove);
          handle.removeEventListener('pointerup', onUp);
          handle.removeEventListener('pointercancel', onUp);
          commit();
        }

        handle.addEventListener('pointermove', onMove);
        handle.addEventListener('pointerup', onUp);
        handle.addEventListener('pointercancel', onUp);
      });
    });

    paint();
  }

  /* ----------------------------------------------------------------------- *
   * Events
   * ----------------------------------------------------------------------- */

  function onClick(event) {
    var el = root();
    if (!el) return;

    var toggle = event.target.closest('.ais-facets-button');
    if (toggle && el.contains(toggle)) {
      var facets = el.querySelector('.ais-facets');
      if (!facets) return;
      var shown = facets.classList.toggle('ais-facets__shown');
      toggle.textContent = shown
        ? toggle.dataset.hideLabel || 'Hide filters'
        : toggle.dataset.showLabel || 'Show filters';
      return;
    }

    var link = event.target.closest('[data-filter-link]');

    // The visible checkbox is an `:after` pseudo-element on `.ais-facet--item`,
    // not on the anchor, and the overrides render the label `display:inline` —
    // so a click on the box lands on the <li>. Widen the hit area to the whole
    // row, which is how the InstantSearch widget behaved.
    if (!link) {
      var row = event.target.closest('.ais-facet--item');
      if (row) link = row.querySelector('[data-filter-link]');
    }

    if (link && el.contains(link)) {
      event.preventDefault();
      render(link.getAttribute('href'), {scrollToTop: false});
    }
  }

  function onChange(event) {
    var el = root();
    if (!el) return;

    var select = event.target.closest('[data-sort-select]');
    if (!select || !el.contains(select)) return;

    var url = new URL(window.location.href);
    url.searchParams.delete('page');
    if (select.value) {
      url.searchParams.set('sort_by', select.value);
    } else {
      url.searchParams.delete('sort_by');
    }
    render(url.toString(), {scrollToTop: true});
  }

  function init() {
    if (!root()) return;
    if (migrateLegacyParams()) return;

    readjust();
    initPriceSliders();
    observeSentinel();

    document.addEventListener('click', onClick);
    document.addEventListener('change', onChange);
    window.addEventListener('resize', readjust);
    window.addEventListener('popstate', function () {
      render(window.location.href, {pushState: false, scrollToTop: false});
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
